import React, { useState, useEffect } from 'react';
import { adminAPI, auditAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import CreateUserModal from '../components/CreateUserModal';
import BulkImportModal from '../components/BulkImportModal';
import {
  Users,
  Shield,
  UserCheck,
  UserX,
  Search,
  AlertTriangle,
  Clock,
  History,
  Settings,
  UserPlus,
  Upload,
  Mail,
  Key,
} from 'lucide-react';
import PermissionsManagerModal from '../components/PermissionsManagerModal';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [permissionsModalUser, setPermissionsModalUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadAuditLogs();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      const res = await adminAPI.getUsers();
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const res = await auditAPI.getLogs({ limit: 50 });
      setAuditLogs(res.data);
    } catch (error) {
      console.error('Failed to load audit logs');
    }
  };

  const handleToggleStatus = async (userId, currentActive) => {
    try {
      await adminAPI.updateUserStatus(userId, !currentActive);
      toast.success(`User ${currentActive ? 'deactivated' : 'activated'}`);
      loadUsers();
      loadAuditLogs();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset password for ${userName}? They will need to change it on next login.`)) {
      return;
    }
    try {
      await adminAPI.resetUserPassword(userId);
      toast.success(`Password reset for ${userName}`);
      loadAuditLogs();
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleResendInvite = async (userId, userName) => {
    try {
      const response = await adminAPI.resendInvite(userId);
      toast.success(response.data.message);
      loadAuditLogs();
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleUserCreated = (newUser) => {
    loadUsers();
    loadAuditLogs();
  };

  const handleBulkImportComplete = (results) => {
    loadUsers();
    loadAuditLogs();
  };

  const filteredUsers = users.filter(u => {
    // Filter by search term
    const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by active status (hide inactive by default)
    const matchesActiveFilter = showInactiveUsers || u.active !== false;
    
    return matchesSearch && matchesActiveFilter;
  });

  // Count inactive users for badge
  const inactiveCount = users.filter(u => u.active === false).length;

  if (!isAdmin) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Access Restricted</h2>
        <p className="text-slate-600">Only administrators can access user management.</p>
      </div>
    );
  }

  const roleColors = {
    admin: 'bg-purple-100 text-purple-700 border-purple-200',
    manager: 'bg-blue-100 text-blue-700 border-blue-200',
    technician: 'bg-green-100 text-green-700 border-green-200',
    site_worker: 'bg-orange-100 text-orange-700 border-orange-200',
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          User Management
        </h1>
        <p className="text-slate-600">Manage user roles and access permissions</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Users className="w-4 h-4" />
            Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === 'audit'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <History className="w-4 h-4" />
            Audit Log
          </button>
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              {/* Show Inactive Users Toggle */}
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={showInactiveUsers}
                  onChange={(e) => setShowInactiveUsers(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-slate-600">Show Inactive</span>
                {inactiveCount > 0 && (
                  <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">
                    {inactiveCount}
                  </span>
                )}
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                <UserPlus className="w-4 h-4" />
                Create User
              </button>
              <button
                onClick={() => setShowBulkImportModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                <Upload className="w-4 h-4" />
                Bulk Import
              </button>
            </div>
          </div>

          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">User</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Role</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredUsers.map((u) => (
                <tr key={u.id} className={`hover:bg-slate-50 ${u.active === false ? 'opacity-60 bg-slate-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.active === false ? 'bg-slate-300' : 'bg-slate-200'}`}>
                        <span className="text-slate-600 font-medium">
                          {u.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-900">{u.name}</span>
                        {u.active === false && (
                          <span className="text-xs text-red-600">Inactive</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${roleColors[u.role] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                      <Shield className="w-3 h-3" />
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Manage Permissions & Menus — visible for all users (incl. self) */}
                      <button
                        onClick={() => setPermissionsModalUser(u)}
                        className="p-1.5 hover:bg-blue-100 rounded"
                        title="Manage Permissions & Menus"
                        data-testid={`manage-perms-${u.id}`}
                      >
                        <Settings className="w-4 h-4 text-blue-600" />
                      </button>
                    {u.id !== user?.id && (
                      <>
                        <button
                          onClick={() => handleResetPassword(u.id, u.name)}
                          className="p-1.5 hover:bg-amber-100 rounded"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4 text-amber-600" />
                        </button>
                        <button
                          onClick={() => handleResendInvite(u.id, u.name)}
                          className="p-1.5 hover:bg-purple-100 rounded"
                          title="Resend Invitation"
                        >
                          <Mail className="w-4 h-4 text-purple-600" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(u.id, u.active !== false)}
                          className={`p-1.5 rounded ${u.active !== false ? 'hover:bg-red-100' : 'hover:bg-green-100'}`}
                          title={u.active !== false ? 'Deactivate' : 'Activate'}
                        >
                          {u.active !== false ? (
                            <UserX className="w-4 h-4 text-red-600" />
                          ) : (
                            <UserCheck className="w-4 h-4 text-green-600" />
                          )}
                        </button>
                      </>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Recent Activity</h2>
            <p className="text-sm text-slate-500">Last 50 actions in the system</p>
          </div>
          <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
            {auditLogs.map((log) => (
              <div key={log.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-slate-900">
                      {log.user_email || 'System'}
                      <span className="font-normal text-slate-600"> {log.action} </span>
                      <span className="text-slate-900">{log.resource_type}</span>
                      {log.resource_name && (
                        <span className="text-slate-600"> ({log.resource_name})</span>
                      )}
                    </p>
                    {log.details && (
                      <p className="text-sm text-slate-500 mt-1">
                        {JSON.stringify(log.details)}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {format(new Date(log.timestamp), 'MMM dd, HH:mm')}
                  </span>
                </div>
              </div>
            ))}
            {auditLogs.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                <History className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No audit logs yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onUserCreated={handleUserCreated}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <BulkImportModal
          onClose={() => setShowBulkImportModal(false)}
          onImportComplete={handleBulkImportComplete}
        />
      )}

      {/* Access Model V2 — Permissions Manager */}
      <PermissionsManagerModal
        user={permissionsModalUser}
        isOpen={!!permissionsModalUser}
        onClose={() => setPermissionsModalUser(null)}
        onSaved={loadUsers}
      />
    </div>
  );
};

export default UserManagement;
